import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { InventoryItem } from '@/lib/api/inventory-api'
import { inventoryApi } from '@/lib/api/inventory-api'
import { productLotApi } from '@/lib/api/product-lot-api'

export const inventoryKeys = {
    all: ['inventory'] as const,
    product: (productId: string) =>
        [...inventoryKeys.all, 'product', productId] as const,
    lowStock: (warehouseId: string) =>
        [...inventoryKeys.all, 'low-stock', warehouseId] as const,
    warehouses: ['warehouses'] as const,
}

/**
 * @deprecated `/inventory/product/:id` đã GỠ sau product_lots redesign.
 * Trả empty array để các trang đang import không crash. Cần refactor sang
 * `/product-lots?product_id=X` khi UI tồn kho per-warehouse được thiết kế lại
 * cho product_lots (warehouse + lot dimension thay vì aggregate).
 */
export function useProductInventory(_productId: string) {
    return useQuery({
        queryKey: ['inventory', 'product', _productId, 'deprecated'] as const,
        queryFn: () => [] as InventoryItem[],
        enabled: false,
    })
}

/** Tồn kho 1 kho. Dùng cho form tạo đơn để hiện số lượng khả dụng trong
 * dropdown chọn SP. Nguồn mới là product_lots vì `/inventory` legacy đã gỡ. */
export function useInventoryByWarehouse(warehouseId: string | undefined) {
    return useQuery({
        queryKey: [...inventoryKeys.all, 'by-warehouse', warehouseId] as const,
        queryFn: async () => {
            const res = await productLotApi.listLots({
                warehouse_id: warehouseId!,
                only_available: '1',
                per_page: 500,
            })
            const rows = res.data ?? []
            const byProduct = new Map<string, InventoryItem>()

            for (const lot of rows) {
                const existing = byProduct.get(lot.product_id)
                if (existing) {
                    existing.quantity += lot.quantity_available
                    continue
                }
                byProduct.set(lot.product_id, {
                    id: `${lot.product_id}:${warehouseId!}`,
                    product_id: lot.product_id,
                    warehouse_id: warehouseId!,
                    quantity: lot.quantity_available,
                    min_stock_level: 0,
                    max_stock_level: 0,
                    updated_at: '',
                })
            }

            return Array.from(byProduct.values())
        },
        enabled: !!warehouseId,
        staleTime: 60 * 1000,
    })
}

export function useCreateAdjustment() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: inventoryApi.createAdjustment,
        onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
    })
}

export function useUpdateThresholds() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: inventoryApi.updateThresholds,
        onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.all }),
    })
}

/** Fetch all active warehouses. The endpoint is auth-only (no extra permission required). */
export function useWarehouses() {
    return useQuery({
        queryKey: inventoryKeys.warehouses,
        queryFn: async () => {
            const res = await inventoryApi.listWarehouses()
            return res.data ?? []
        },
        staleTime: 5 * 60 * 1000, // warehouse list rarely changes
    })
}

/**
 * Fetch low-stock items for a specific warehouse. Returns [] when warehouseId
 * is not yet known (skips the query). Quantities only — no value/cost fields.
 */
export function useLowStockAlerts(warehouseId: string | undefined) {
    return useQuery({
        queryKey: inventoryKeys.lowStock(warehouseId ?? ''),
        queryFn: async () => {
            const res = await inventoryApi.getLowStock(warehouseId!)
            return res.data ?? []
        },
        enabled: !!warehouseId,
    })
}
